import RAPIER from "@dimforge/rapier3d-compat";

export const BODY_STRIDE = 15;

export const BODY_MUTATORS = [
  "lockTranslations", "lockRotations", "setEnabledTranslations", "restrictTranslations", "setEnabledRotations",
  "restrictRotations", "setDominanceGroup", "setAdditionalSolverIterations", "enableCcd", "setSoftCcdPrediction",
  "setTranslation", "setLinvel", "setGravityScale", "setRotation", "setAngvel", "setNextKinematicTranslation",
  "setNextKinematicRotation", "sleep", "wakeUp", "setEnabled", "setBodyType", "setLinearDamping",
  "recomputeMassPropertiesFromColliders", "setAdditionalMass", "setAdditionalMassProperties", "setAngularDamping",
  "resetForces", "resetTorques", "addForce", "applyImpulse", "addTorque", "applyTorqueImpulse", "addForceAtPoint",
  "applyImpulseAtPoint",
] as const;

export const COLLIDER_MUTATORS = [
  "setSensor", "setShape", "setEnabled", "setRestitution", "setFriction", "setFrictionCombineRule",
  "setRestitutionCombineRule", "setCollisionGroups", "setSolverGroups", "setContactSkin", "setActiveHooks",
  "setActiveEvents", "setContactForceEventThreshold", "setActiveCollisionTypes", "setDensity", "setMass",
  "setMassProperties", "setTranslation", "setTranslationWrtParent", "setRotation", "setRotationWrtParent",
  "setHalfExtents", "setRadius", "setRoundRadius", "setHalfHeight",
] as const;

export const WORLD_SETTERS = [ "numSolverIterations", "numInternalPgsIterations", "maxCcdSubsteps", "lengthUnit" ] as const;

export type RigidMotion = {
  rotate: boolean;
  planar: boolean;
  px: number; py: number; pz: number;
  qx: number; qy: number; qz: number; qw: number;
  dx: number; dy: number; dz: number;
};

export type Command =
  | { o: "mv"; h: number[]; m: RigidMotion }
  | { o: "cb"; h: number; d: Record<string, unknown> }
  | { o: "cc"; h: number; p: number | null; d: Record<string, unknown> }
  | { o: "rb"; h: number }
  | { o: "rc"; h: number; w: boolean }
  | { o: "b"; h: number; m: string; a: unknown[] }
  | { o: "c"; h: number; m: string; a: unknown[] }
  | { o: "w"; k: string; v: unknown };

export type ToWorker =
  | { type: "reset"; seq: number; snapshot: Uint8Array }
  | { type: "step"; seq: number; dt: number; substeps: number; cmds: Command[]; track: number[]; untrack: number[] };

export type FromWorker =
  | { type: "ready" }
  | { type: "state"; seq: number; bodies: Float64Array; contacts: Float64Array; step_ms: number }
  | { type: "error"; seq: number; message: string };

export function copyArg( value: unknown ): unknown
{
  if ( value === null || typeof value !== "object" )
    return value;
  if ( ArrayBuffer.isView( value ) )
    return ( value as Float32Array ).slice();
  if ( Array.isArray( value ) )
    return value.map( copyArg );
  const shape = value as { type?: unknown; intoRaw?: unknown };
  if ( typeof shape.intoRaw === "function" && typeof shape.type === "number" )
    return serializeShape( value as RAPIER.Shape );
  const v = value as { x?: unknown; y?: unknown; z?: unknown; w?: unknown };
  if ( typeof v.x === "number" && typeof v.y === "number" )
  {
    const out: Record<string, number> = { x: v.x, y: v.y };
    if ( typeof v.z === "number" ) out.z = v.z;
    if ( typeof v.w === "number" ) out.w = v.w;
    return out;
  }
  const out: Record<string, unknown> = {};
  for ( const key of Object.keys( value ) )
    out[key] = copyArg( ( value as Record<string, unknown> )[key] );
  return out;
}

export function serializeShape( shape: RAPIER.Shape ): Record<string, unknown>
{
  const out: Record<string, unknown> = { __shape: true };
  for ( const key of Object.keys( shape ) )
    out[key] = copyArg( ( shape as unknown as Record<string, unknown> )[key] );
  return out;
}

const SHAPE_CLASSES: Record<number, unknown> = {
  [RAPIER.ShapeType.Ball]: RAPIER.Ball,
  [RAPIER.ShapeType.Cuboid]: RAPIER.Cuboid,
  [RAPIER.ShapeType.Capsule]: RAPIER.Capsule,
  [RAPIER.ShapeType.Segment]: RAPIER.Segment,
  [RAPIER.ShapeType.Triangle]: RAPIER.Triangle,
  [RAPIER.ShapeType.TriMesh]: RAPIER.TriMesh,
  [RAPIER.ShapeType.ConvexPolyhedron]: RAPIER.ConvexPolyhedron,
  [RAPIER.ShapeType.Cylinder]: RAPIER.Cylinder,
  [RAPIER.ShapeType.Cone]: RAPIER.Cone,
  [RAPIER.ShapeType.RoundCuboid]: RAPIER.RoundCuboid,
  [RAPIER.ShapeType.RoundCylinder]: RAPIER.RoundCylinder,
  [RAPIER.ShapeType.RoundCone]: RAPIER.RoundCone,
};

export function restoreArg( value: unknown ): unknown
{
  if ( value === null || typeof value !== "object" || ArrayBuffer.isView( value ) )
    return value;
  if ( Array.isArray( value ) )
    return value.map( restoreArg );
  const record = value as Record<string, unknown>;
  if ( record.__shape === true )
  {
    const cls = SHAPE_CLASSES[record.type as number] as { prototype: object } | undefined;
    if ( !cls )
      throw new Error( `unsupported shape type ${record.type}` );
    const shape = Object.create( cls.prototype ) as Record<string, unknown>;
    for ( const key of Object.keys( record ) )
      if ( key !== "__shape" )
        shape[key] = restoreArg( record[key] );
    return shape;
  }
  return value;
}

export function serializeBodyDesc( desc: RAPIER.RigidBodyDesc ): Record<string, unknown>
{
  return copyArg( desc ) as Record<string, unknown>;
}

export function restoreBodyDesc( data: Record<string, unknown> ): RAPIER.RigidBodyDesc
{
  const desc = new RAPIER.RigidBodyDesc( data.status as RAPIER.RigidBodyType );
  Object.assign( desc, data );
  return desc;
}

export function serializeColliderDesc( desc: RAPIER.ColliderDesc ): Record<string, unknown>
{
  return copyArg( desc ) as Record<string, unknown>;
}

export function restoreColliderDesc( data: Record<string, unknown> ): RAPIER.ColliderDesc
{
  const shape = restoreArg( data.shape ) as RAPIER.Shape;
  const desc = new RAPIER.ColliderDesc( shape );
  for ( const key of Object.keys( data ) )
    if ( key !== "shape" )
      ( desc as unknown as Record<string, unknown> )[key] = data[key];
  return desc;
}

const motion_vec = { x: 0, y: 0, z: 0 };
const motion_quat = { x: 0, y: 0, z: 0, w: 1 };

function rotateInto( m: RigidMotion, vx: number, vy: number, vz: number ): void
{
  const qx = m.qx, qy = m.qy, qz = m.qz, qw = m.qw;
  const tx = 2 * ( qy * vz - qz * vy );
  const ty = 2 * ( qz * vx - qx * vz );
  const tz = 2 * ( qx * vy - qy * vx );
  motion_vec.x = vx + qw * tx + qy * tz - qz * ty;
  motion_vec.y = vy + qw * ty + qz * tx - qx * tz;
  motion_vec.z = vz + qw * tz + qx * ty - qy * tx;
}

export function applyRigidMotion( body: RAPIER.RigidBody, m: RigidMotion ): void
{
  const p = body.translation();
  if ( !m.rotate )
  {
    body.setTranslation( { x: p.x + m.dx, y: p.y + m.dy, z: p.z + m.dz }, false );
    return;
  }

  if ( m.planar )
  {
    rotateInto( m, p.x - m.px, 0, p.z - m.pz );
    body.setTranslation( { x: m.px + motion_vec.x, y: p.y, z: m.pz + motion_vec.z }, false );
  }
  else
  {
    rotateInto( m, p.x - m.px, p.y - m.py, p.z - m.pz );
    body.setTranslation( { x: m.px + motion_vec.x, y: m.py + motion_vec.y, z: m.pz + motion_vec.z }, false );
  }

  const r = body.rotation();
  const qax = m.qx, qay = m.qy, qaz = m.qz, qaw = m.qw;
  motion_quat.x = qax * r.w + qaw * r.x + qay * r.z - qaz * r.y;
  motion_quat.y = qay * r.w + qaw * r.y + qaz * r.x - qax * r.z;
  motion_quat.z = qaz * r.w + qaw * r.z + qax * r.y - qay * r.x;
  motion_quat.w = qaw * r.w - qax * r.x - qay * r.y - qaz * r.z;
  body.setRotation( { x: motion_quat.x, y: motion_quat.y, z: motion_quat.z, w: motion_quat.w }, false );

  if ( body.isSleeping() )
    return;

  const lv = body.linvel();
  rotateInto( m, lv.x, lv.y, lv.z );
  body.setLinvel( { x: motion_vec.x, y: motion_vec.y, z: motion_vec.z }, false );

  const av = body.angvel();
  rotateInto( m, av.x, av.y, av.z );
  body.setAngvel( { x: motion_vec.x, y: motion_vec.y, z: motion_vec.z }, false );
}
